import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  makeStyles,
  Popover,
  Typography,
  useTheme,
  useMediaQuery
} from '@material-ui/core';
import { green } from '@material-ui/core/colors';
import PropTypes from 'prop-types';
import { useRef, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FilterBox from 'src/app/components/FilterBox';
import { DEVICE_STATUS, PAGE_SIZE_LIST } from 'src/app/constant/config';
import {
  getListDevice,
  importTransferDevice,
  resetResultImport
} from 'src/features/deviceSlice';
import CreateDevice from './CreateDevice';
import { getListDeviceVersion } from 'src/features/versionDeviceSlice';
import SearchInput from '../../components/input/SearchInput';

const Toolbar = ({ searchRef, isLoading, listAgencies }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const isAdmin = useSelector(state => state.authSlice.isAdmin);
  const isAgency = useSelector(state => state.authSlice.isAgency);
  const isAccountant = useSelector(state => state.authSlice.isAccountant);
  const isSale = useSelector(state => state.authSlice.isSale);
  const isLeadMarketing = useSelector(state => state.authSlice.isLeadMarketing);
  const isCustomerCare = useSelector(state => state.authSlice.isCustomerCare);
  const isTechnician = useSelector(state => state.authSlice.isTechnician);
  const hasManagePermission = isAdmin || isAgency || isCustomerCare;
  const resultTransferImport = useSelector(
    state => state.deviceSlice.resultTransferImport
  );

  const [filterActive, setFilterActive] = useState(true);
  const [filterInactive, setFilterInactive] = useState(true);

  useEffect(() => {
    if (!resultTransferImport) return;
    const blob = new Blob([resultTransferImport]);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report_import_transfer_${Date.now()}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);

    dispatch(resetResultImport(resetResultImport));
  }, [resultTransferImport, dispatch]);

  const fileInput = useRef(null);

  const [query, setQuery] = useState({
    agency_id: undefined,
    device_type: undefined,
    status: undefined,
    sub: undefined,
    keywords: undefined,
    page: 1,
    page_size: PAGE_SIZE_LIST
  });

  const getListDeviceVersionWithParams = useCallback(
    data => {
      dispatch(getListDeviceVersion(data));
    },
    [dispatch]
  );

  // Xử lý thay đổi Checkbox Kích hoạt / Lưu kho
  const handleStatusFilterChange = useCallback(
    (type, checked) => {
      let nextActive = filterActive;
      let nextInactive = filterInactive;

      if (type === 'active') {
        nextActive = checked;
        setFilterActive(checked);
      } else if (type === 'inactive') {
        nextInactive = checked;
        setFilterInactive(checked);
      }
      let statusValue = undefined;
      const activeStatus = DEVICE_STATUS?.ACTIVE || 'active';
      const inactiveStatus = DEVICE_STATUS?.INACTIVE || 'inactive';

      if (nextActive && nextInactive) {
        statusValue = undefined;
      } else if (nextActive && !nextInactive) {
        statusValue = activeStatus;
      } else if (!nextActive && nextInactive) {
        statusValue = inactiveStatus;
      } else {
        statusValue = 'none';
      }

      setQuery(prevQuery => {
        const newQuery = {
          ...prevQuery,
          status: statusValue,
          page: 1
        };
        if (searchRef) {
          searchRef(newQuery);
        }
        return newQuery;
      });
    },
    [filterActive, filterInactive, searchRef]
  );

  // Memoize handlers để tối ưu hiệu năng
  const handleSearchChange = useCallback(value => {
    setQuery(prevQuery => ({
      ...prevQuery,
      keywords: value || undefined,
      page: 1
    }));
  }, []);

  const handleSearch = useCallback(
    value => {
      setQuery(prevQuery => {
        const newQuery = {
          ...prevQuery,
          keywords: value || undefined,
          page: 1
        };
        if (searchRef) {
          searchRef(newQuery);
        }
        return newQuery;
      });
    },
    [searchRef]
  );

  const handleClickFilter = useCallback(event => {
    setAnchorEl(event.currentTarget);
  }, []);

  const open = Boolean(anchorEl);
  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleOpenCreateModal = useCallback(() => {
    setOpenCreateModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setOpenCreateModal(false);
  }, []);

  const upload = useCallback(
    file => {
      if (file) {
        const formData = new FormData();
        formData.append('file', file, file.name);
        dispatch(importTransferDevice(formData));
        document.getElementById('importTransferDevice').value = '';
        dispatch(getListDevice(query));
      }
    },
    [dispatch, query]
  );

  const onFileChange = useCallback(
    event => {
      upload(event.target.files[0]);
    },
    [upload]
  );

  return (
    <div className={classes.root}>
      {openCreateModal && (
        <CreateDevice
          open={openCreateModal}
          onClose={closeModal}
          getListVersion={getListDeviceVersionWithParams}
        />
      )}
      <Box>
        <Box className={classes.groupSearch}>
          <div className="col-12 col-md-3">
            <Typography className={classes.title}>Quản trị thiết bị</Typography>
          </div>
          <div className="col-12 col-md-9 d-flex justify-content-end align-items-center flex-wrap">
            <SearchInput
              value={query.keywords || ''}
              onChange={handleSearchChange}
              onSearch={handleSearch}
              className={'searchInputDevice'}
              placeholder="Serial hoặc biển số"
            />

            {(isAdmin || isAgency) && (
              <Box className={classes.statusFilterGroup}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filterActive}
                      onChange={e =>
                        handleStatusFilterChange('active', e.target.checked)
                      }
                      className={classes.checkboxSecondary}
                      size="small"
                    />
                  }
                  label="Kích hoạt"
                  classes={{ label: classes.checkboxLabel }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filterInactive}
                      onChange={e =>
                        handleStatusFilterChange('inactive', e.target.checked)
                      }
                      className={classes.checkboxSecondary}
                      size="small"
                    />
                  }
                  label="Lưu kho"
                  classes={{ label: classes.checkboxLabel }}
                />
              </Box>
            )}

            {(isAdmin || isAgency || isAccountant || isSale || isLeadMarketing || isTechnician) && (
              <Typography
                color="secondary"
                size="small"
                variant="contained"
                className="mx-3 cursor-pointer d-inline-flex align-items-center"
              >
                <span
                  className="cursor-pointer d-inline-flex align-items-center"
                  aria-describedby="filter-module"
                  onClick={handleClickFilter}
                  style={{ fontSize: '15px', fontWeight: 'bold' }}
                >
                  <img
                    src={`/static/iconSvg/filter.svg`}
                    alt="filter"
                    style={{ paddingRight: '5px' }}
                  />
                  &nbsp; Lọc
                </span>

                <Popover
                  id="filter-module"
                  open={open}
                  keepMounted
                  anchorEl={anchorEl}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                  }}
                >
                  <FilterBox
                    listAgencies={listAgencies}
                    searchRef={searchRef}
                    isAdmin={isAdmin}
                    isCustomerCare={isCustomerCare}
                    hasManagePermission={hasManagePermission}
                  />
                </Popover>
              </Typography>
            )}

            {(isAdmin || isAgency || isAccountant || isSale || isLeadMarketing || isTechnician) && (
              <>
                <input
                  name="file"
                  ref={fileInput}
                  id="importTransferDevice"
                  onChange={onFileChange}
                  type="file"
                  hidden
                />
                <Typography
                  onClick={() => fileInput.current.click()}
                  color="secondary"
                  size="small"
                  variant="contained"
                  className="mx-3 cursor-pointer d-inline-flex align-items-center"
                >
                  <span
                    className="d-inline-flex align-items-center"
                    style={{ fontSize: '15px', fontWeight: 'bold' }}
                  >
                    <img
                      src={`/static/iconSvg/import.svg`}
                      alt="import"
                      style={{ paddingRight: '5px' }}
                    />
                    &nbsp; {isTechnician ? 'Nhập kho từ file' : 'Chuyển kho từ file'}
                  </span>
                </Typography>
                {(isAdmin || isAccountant || isSale || isLeadMarketing || isTechnician) && (
                  <Button
                    onClick={handleOpenCreateModal}
                    style={{
                      ...(isMobile && { lineHeight: '18px' }),
                      marginLeft: '10px',
                      color: '#ffff',
                      height: '40px',
                      textTransform: 'none'
                    }}
                    color="secondary"
                    variant="contained"
                  >
                    {isMobile ? 'Thêm thiết bị' : 'Thêm mới thiết bị'}
                  </Button>
                )}
              </>
            )}
          </div>
        </Box>
      </Box>
    </div>
  );
};

Toolbar.propTypes = {
  className: PropTypes.string,
  searchRef: PropTypes.func,
  isLoading: PropTypes.bool,
  listAgencies: PropTypes.array
};

const useStyles = makeStyles(theme => ({
  root: {},
  importButton: {
    marginRight: theme.spacing(1)
  },
  wrapper: {
    margin: theme.spacing(1),
    position: 'relative'
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12
  },
  exportButton: {
    marginRight: theme.spacing(1)
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#0C1132',
    [theme.breakpoints.down('sm')]: {
      fontSize: '18px',
      fontWeight: '500'
    }
  },
  groupSearch: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minWidth: '50%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      width: '100%',
      gap: '8px'
    }
  },
  groupSearchLeft: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start'
  },
  searchInput: {
    width: '353px'
  },

  statusFilterGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '190px',
    margin: '0 12px',
    height: '40px', // Khóa chiều cao bằng ô search và nút thêm mới để thẳng trục giữa
    '& .MuiFormControlLabel-root': {
      margin: 0,
      display: 'inline-flex',
      alignItems: 'center' // Căn giữa checkbox với label của nó
    },

    [theme.breakpoints.down('sm')]: {
      width: '100%',
      margin: `${theme.spacing(1)}px 0`,
      height: 'auto'
    }
  },
  checkboxSecondary: {
    color: theme.palette.secondary.main,
    padding: '4px',
    '&.Mui-checked': {
      color: theme.palette.secondary.main 
    }
  },
  checkboxLabel: {
    fontSize: '14px',
    fontWeight: 'bold',                  
    color: theme.palette.secondary.main, 
    userSelect: 'none',
    lineHeight: 1 // Giúp chữ không bị giật baseline so với icon checkbox
  },
  exportBtn: {
    justifyContent: 'flex-end',
    display: 'flex',
    paddingTop: '10px',
    paddingBottom: '10px'
  },
  shadowBox: {
    boxShadow: '0 2px 5px rgba(0,0,0,.18)'
  }
}));

export default Toolbar;